import type {
  ProfitabilityDocument,
  ProfitabilityRun,
  ReportInputSlot,
} from "@/profitability/types";

export type DocumentSlot = Pick<
  ReportInputSlot,
  "key" | "title" | "description"
>;

/**
 * One line of the period's documents: a template input with the file that
 * filled it (or none yet), a file the run read without a slot, or the
 * owner's own numbers for the period.
 */
export interface DocumentEntry {
  /** Stable across renders: the slot's key, else the document's id. */
  key: string;
  role: "input" | "goal";
  slot: DocumentSlot | null;
  document: ProfitabilityDocument | null;
}

export type RunDocuments = Pick<ProfitabilityRun, "documents" | "inputs"> & {
  goal_document?: ProfitabilityRun["goal_document"];
};

/**
 * The period's documents in the order the template reads them: each input
 * slot in its order, with the run's file for it (or none, "Not uploaded");
 * then any file the run read under no slot (a template without inputs reads
 * files by name); then the owner's own numbers, when given.
 */
export const documentEntries = (
  slots: DocumentSlot[],
  run: RunDocuments,
): DocumentEntry[] => {
  const documents = run.documents ?? [];
  const inputs = run.inputs ?? {};
  const slotted = slots.map<DocumentEntry>((slot) => ({
    key: `slot:${slot.key}`,
    role: "input",
    slot,
    document: documents.find((item) => item.id === inputs[slot.key]) ?? null,
  }));
  const used = new Set(
    slotted.flatMap((entry) => (entry.document ? [entry.document.id] : [])),
  );
  const loose = documents
    .filter((document) => !used.has(document.id))
    .map<DocumentEntry>((document) => {
      // A file in a slot the template no longer declares keeps its key.
      const key = Object.entries(inputs).find(
        ([, id]) => id === document.id,
      )?.[0];
      return {
        key: document.id,
        role: "input",
        slot: key ? { key, title: key } : null,
        document,
      };
    });
  const goal = run.goal_document
    ? [
        {
          key: run.goal_document.id,
          role: "goal" as const,
          slot: null,
          document: run.goal_document,
        },
      ]
    : [];
  return [...slotted, ...loose, ...goal];
};

export const selectedEntry = (
  entries: DocumentEntry[],
  documentId: string | null,
) =>
  entries.find((entry) => documentId && entry.document?.id === documentId) ??
  entries.find((entry) => entry.document) ??
  null;

export const entryPurpose = (entry: DocumentEntry) =>
  entry.role === "goal" ? "Your numbers" : (entry.slot?.title ?? null);

export const documentRows = (document: ProfitabilityDocument) =>
  document.sheets.reduce((sum, sheet) => sum + sheet.row_count, 0);

export const rowsLabel = (count: number) =>
  `${count.toLocaleString()} ${count === 1 ? "row" : "rows"}`;
